import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from "@angular/router";
import { AuthOktaService } from "../../../shared/services/auth/auth-okta.service";
import { AppConstant } from "../../../shared/utility/app-constant";
import { NotificationService } from "../../../shared/services/notification/notification.service";
import { HttpResponseMessage } from "../../../shared/utility/http-response-message";

@Component({
  selector: 'app-verify-email',
  templateUrl: './verify-email.component.html',
  styleUrls: ['./verify-email.component.scss']
})
export class VerifyEmailComponent implements OnInit, OnDestroy {

  @Input() userEmail?: string;
  email?: string;

  countdownSeconds: number = 0;
  isResendDisabled: boolean = false;
  private countdownInterval: any;
  private readonly COUNTDOWN_KEY = 'verify_email_countdown_end';

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authOktaService: AuthOktaService,
    private notificationService: NotificationService
  ) { }

  ngOnInit(): void {
    this.extractEmailFromUrl();
    this.restoreCountdown();
  }

  private extractEmailFromUrl(): void {
    this.route.queryParams.subscribe(params => {
      const errorDescription = params.errorDescription;
      if (errorDescription) {
        const emailMatch = errorDescription.match(/user_email=([^&\s]+)/);
        if (emailMatch && emailMatch[1]) {
          this.email = decodeURIComponent(emailMatch[1]);
        }
      }
    });
  }

  private restoreCountdown(): void {
    const endTimeStr = localStorage.getItem(this.COUNTDOWN_KEY);
    if (!endTimeStr) {
      return;
    }

    const endTime = parseInt(endTimeStr, 10);
    const now = Date.now();
    const remainingMs = endTime - now;

    if (remainingMs > 0) {
      // Countdown is still active
      this.countdownSeconds = Math.ceil(remainingMs / 1000);
      this.isResendDisabled = true;
      this.startCountdownInterval();
    } else {
      // Countdown expired while page was closed
      localStorage.removeItem(this.COUNTDOWN_KEY);
    }
  }

  onResend(): void {
    if (this.isResendDisabled) {
      return;
    }

    this.authOktaService.resendEmailVerification(this.email).subscribe((res: any) => {
      if (res.status === AppConstant.HTTP_RESPONSE_STATUS_SUCCESS) {
        this.notificationService.successMessage(HttpResponseMessage.EMAIL_VERIFICATION_MAIL_SENT_SUCCESSFULLY);
      } else {
        this.notificationService.infoMessage(res.body.message);
      }
    }, (error => {
      this.notificationService.errorMessage(error);
    })
    );

    this.startCountdown();
  }

  private startCountdown(): void {
    // Calculate end time (60 seconds from now)
    const endTime = Date.now() + (60 * 1000);
    localStorage.setItem(this.COUNTDOWN_KEY, endTime.toString());

    this.countdownSeconds = 60;
    this.isResendDisabled = true;
    this.startCountdownInterval();
  }

  private startCountdownInterval(): void {
    this.countdownInterval = setInterval(() => {
      this.countdownSeconds--;

      if (this.countdownSeconds <= 0) {
        this.stopCountdown();
      }
    }, 1000);
  }

  private stopCountdown(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
    this.countdownSeconds = 0;
    this.isResendDisabled = false;
    localStorage.removeItem(this.COUNTDOWN_KEY);
  }

  ngOnDestroy(): void {
    this.stopCountdown();
  }

  onBackToLogin(): void {
    this.authOktaService.signIn();
  }

  onLogout(): void {
    this.authOktaService.signOut();
  }
}
